import { animate, state, style, transition, trigger } from '@angular/animations';
import { LowerCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { ACTION_LABELS, PERMISSION_GROUPS } from '../../../models/permission.model';
import { UserRole } from '../../../models/user-role.model';
import { UserRoleService } from '../../../services/user-role.service';

interface PermissionGroupView {
  key: string;
  label: string;
  permissions: { key: string; value: string; actionLabel: string }[];
}

@Component({
  selector: 'app-permissions-matrix',
  imports: [LowerCasePipe, MatIconButton, MatIcon, MatProgressSpinner],
  templateUrl: './permissions-matrix.component.html',
  styleUrl: './permissions-matrix.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0', opacity: '0', overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: '1', overflow: 'visible' })),
      transition('collapsed <=> expanded', [
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)'),
      ]),
    ]),
  ],
})
export class PermissionsMatrixComponent implements OnInit {
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _roleService = inject(UserRoleService);

  /** Loading state */
  protected readonly loading = signal(true);

  /** All roles fetched from the API */
  protected readonly roles = signal<UserRole[]>([]);

  /** Track which permission groups are expanded */
  protected readonly expandedGroups = signal(new Set<string>());

  /** All permission groups */
  private readonly _allPermissionGroups: PermissionGroupView[] = Object.entries(
    PERMISSION_GROUPS
  ).map(([key, group]) => ({
    key,
    label: group.label,
    permissions: Object.entries(group.permissions).map(([permKey, permValue]) => {
      const action = permValue.split(':')[1];
      return {
        key: permKey,
        value: permValue,
        actionLabel: ACTION_LABELS[action] ?? action,
      };
    }),
  }));

  /** Permission groups (read-only display) */
  protected readonly permissionGroups = computed(() => this._allPermissionGroups);

  /** Pre-computed permission sets per role for O(1) lookups */
  protected readonly rolePermissionSets = computed(() => {
    const map = new Map<string, Set<string>>();
    for (const role of this.roles()) {
      map.set(role._id, new Set(role.permissions));
    }
    return map;
  });

  /** Total number of available permissions */
  protected readonly totalPermissionCount = this._allPermissionGroups.reduce(
    (sum, group) => sum + group.permissions.length,
    0
  );

  /** Colspan for the full table (1 for "Recht" column + number of roles) */
  protected readonly tableColspan = computed(() => 1 + this.roles().length);

  /** Minimum table width to prevent column squishing */
  protected readonly minTableWidth = computed(() => 280 + this.roles().length * 160);

  /** Grid template columns for permission rows inside animation wrapper */
  protected readonly gridTemplateColumns = computed(
    () => `280px repeat(${this.roles().length}, 1fr)`
  );

  ngOnInit(): void {
    // Initialize all groups as expanded
    const allKeys = new Set(this._allPermissionGroups.map((g) => g.key));
    this.expandedGroups.set(allKeys);

    // Load all roles
    this._roleService
      .getList({ params: { sortBy: 'label', sortOrder: 'asc' } })
      .subscribe({
        next: (roles) => {
          this.roles.set(roles);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
        },
      });
  }

  protected navigateBack(): void {
    this._router.navigate(['../'], { relativeTo: this._route });
  }

  // --- Expand / Collapse ---

  protected isGroupExpanded(groupKey: string): boolean {
    return this.expandedGroups().has(groupKey);
  }

  protected toggleGroupExpanded(groupKey: string): void {
    this.expandedGroups.update((set) => {
      const next = new Set(set);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }

  protected expandAll(): void {
    const allKeys = new Set(this._allPermissionGroups.map((g) => g.key));
    this.expandedGroups.set(allKeys);
  }

  protected collapseAll(): void {
    this.expandedGroups.set(new Set());
  }

  // --- Permission helpers (read-only) ---

  protected roleHasPermission(roleId: string, permission: string): boolean {
    return this.rolePermissionSets().get(roleId)?.has(permission) ?? false;
  }

  protected groupSelectedCount(group: PermissionGroupView, roleId: string): number {
    const permSet = this.rolePermissionSets().get(roleId);
    if (!permSet) return 0;
    return group.permissions.filter((p) => permSet.has(p.value)).length;
  }

  protected rolePermissionCount(roleId: string): number {
    return this.rolePermissionSets().get(roleId)?.size ?? 0;
  }
}
